import type { ClientToServerMessage, ServerToClientMessage } from '@all-for-one/shared-types'
import { getWsBaseUrl } from 'util/utils'

export type MessageListener = (msg: ServerToClientMessage) => void
export type ConnectionStateListener = (state: ConnectionState) => void

export interface ConnectionState {
  status: 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'failed'
  reconnectAttempt?: number
  reconnectDelay?: number
  /** Why we will NOT reconnect: evicted = opened in another tab, auth = login rejected */
  closeReason?: 'evicted' | 'auth'
}

export interface WsClient {
  connect(): void
  close(): void
  onMessage(cb: MessageListener): void
  offMessage(cb: MessageListener): void
  send(msg: ClientToServerMessage): void
  onOpen(cb: () => void): void
  offOpen(cb: () => void): void
  onConnectionStateChange(cb: ConnectionStateListener): void
  offConnectionStateChange(cb: ConnectionStateListener): void
  getConnectionState(): ConnectionState
  startSession(classroomId: string, lessonId: string, lessonName: string, eduqueryLessonId: string): void
}

export function createWsClient(url?: string): WsClient {
  const resolvedUrl = url ?? getWsBaseUrl()
  let ws: WebSocket | null = null
  const listeners: Set<MessageListener> = new Set()
  const openListeners: Set<() => void> = new Set()
  const connectionStateListeners: Set<ConnectionStateListener> = new Set()
  const messageQueue: ClientToServerMessage[] = []

  // Reconnection state
  let shouldReconnect = true
  let reconnectAttempt = 0
  let reconnectTimer: NodeJS.Timeout | null = null
  const MAX_RECONNECT_ATTEMPTS = 10
  const MAX_BACKOFF_DELAY = 30000 // 30 seconds

  // Heartbeat state
  let heartbeatInterval: NodeJS.Timeout | null = null
  let lastPongTime: number = Date.now()
  const HEARTBEAT_INTERVAL = 20000 // 20 seconds
  const HEARTBEAT_TIMEOUT = 5000   // 5 seconds to respond

  // Connection state
  let connectionState: ConnectionState = { status: 'disconnected' }

  function updateConnectionState(newState: ConnectionState): void {
    connectionState = newState
    connectionStateListeners.forEach((cb) => {
      try {
        cb(connectionState)
      } catch {
        // isolate listener errors
      }
    })
  }

  function calculateBackoffDelay(attempt: number): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    const delay = Math.min(1000 * Math.pow(2, attempt), MAX_BACKOFF_DELAY)
    return delay
  }

  function attemptReconnect(): void {
    if (!shouldReconnect) return
    if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      // WS-7: 'failed' (not 'disconnected') so pages can stop the infinite
      // "Connecting..." spinner and tell the user to check their internet
      // and refresh.
      console.error('[WebSocket] Max reconnection attempts reached')
      updateConnectionState({ status: 'failed', reconnectAttempt })
      return
    }

    reconnectAttempt++
    const delay = calculateBackoffDelay(reconnectAttempt - 1)

    updateConnectionState({
      status: 'reconnecting',
      reconnectAttempt,
      reconnectDelay: delay
    })

    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempt}/${MAX_RECONNECT_ATTEMPTS})`)

    reconnectTimer = setTimeout(() => {
      console.log(`[WebSocket] Attempting reconnection ${reconnectAttempt}/${MAX_RECONNECT_ATTEMPTS}`)
      connect()
    }, delay)
  }

  function startHeartbeat(): void {
    stopHeartbeat() // Clear any existing heartbeat

    heartbeatInterval = setInterval(() => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return
      }

      // Check if last pong was too long ago
      const timeSinceLastPong = Date.now() - lastPongTime
      if (timeSinceLastPong > HEARTBEAT_INTERVAL + HEARTBEAT_TIMEOUT) {
        console.log('[WebSocket] Heartbeat timeout - connection appears dead')
        // Manually close to trigger reconnection logic (use 3000 = application-specific)
        try {
          ws.close(3000, 'heartbeat_timeout')
        } catch (error) {
          console.log('[WebSocket] Failed to close socket (already closed):', error)
          // Socket already closed/closing - manually trigger onclose handler
          // by calling attemptReconnect directly
          stopHeartbeat()
          attemptReconnect()
        }
        return
      }

      // Send ping
      try {
        send({ type: 'ping', timestamp: Date.now() })
      } catch (error) {
        console.log('[WebSocket] Failed to send ping:', error)
      }
    }, HEARTBEAT_INTERVAL)
  }

  function stopHeartbeat(): void {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
  }

  function connect(): void {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return
    }

    // Clear any pending reconnect timer
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    const isReconnecting = reconnectAttempt > 0
    updateConnectionState({
      status: isReconnecting ? 'reconnecting' : 'connecting',
      reconnectAttempt: isReconnecting ? reconnectAttempt : undefined
    })

    console.log(`[WebSocket] ${isReconnecting ? 'Reconnecting' : 'Connecting'} to ${resolvedUrl}`)

    ws = new WebSocket(resolvedUrl)

    ws.onopen = () => {
      console.log('[WebSocket] Connection established')

      // Reset reconnection state on successful connection
      reconnectAttempt = 0
      updateConnectionState({ status: 'connected' })

      // Start heartbeat to detect dead connections
      lastPongTime = Date.now()
      startHeartbeat()

      // Flush queued messages when connection opens
      while (messageQueue.length > 0) {
        const queuedMsg = messageQueue.shift()
        if (queuedMsg && ws && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify(queuedMsg))
          } catch {
            // Message failed to send - connection may have closed
          }
        }
      }

      openListeners.forEach((cb) => {
        try {
          cb()
        } catch {
          // isolate listener errors
        }
      })
    }

    ws.onmessage = (ev: MessageEvent) => {
      let parsed: unknown
      try {
        const raw = typeof ev.data === 'string' ? ev.data : String(ev.data)
        parsed = JSON.parse(raw)
      } catch {
        return
      }

      // Handle pong for heartbeat
      const msg = parsed as ServerToClientMessage
      if (msg.type === 'pong') {
        lastPongTime = Date.now()
        // Don't pass pong to regular listeners - it's internal
        return
      }

      listeners.forEach((cb) => {
        try {
          cb(msg)
        } catch {
          // isolate listener errors
        }
      })
    }

    ws.onerror = (error) => {
      console.error('[WebSocket] Connection error:', error)
    }

    ws.onclose = (event) => {
      console.log(`[WebSocket] Connection closed (code: ${event.code}, reason: ${event.reason || 'none'})`)

      // Stop heartbeat when connection closes
      stopHeartbeat()

      // WS-5: 4000 = evicted because the same student opened another
      // tab/device. Reconnecting would just evict the other tab back —
      // an infinite fight. Stay down and say why.
      if (event.code === 4000) {
        console.log('[WebSocket] Evicted (connected elsewhere) - not reconnecting')
        shouldReconnect = false
        updateConnectionState({ status: 'disconnected', closeReason: 'evicted' })
        return
      }

      // 1008 = server rejected our auth. Retrying won't fix a bad login.
      if (event.code === 1008) {
        console.log('[WebSocket] Auth rejected - not reconnecting')
        shouldReconnect = false
        updateConnectionState({ status: 'disconnected', closeReason: 'auth' })
        return
      }

      // Only OUR OWN close() is intentional (it sets shouldReconnect=false and
      // closes with 1000). Everything else — including 1001, which the server
      // sends to every client on a DEPLOY — must reconnect, or a deploy
      // silently kills every live class (WS-6).
      if (!shouldReconnect || event.code === 1000) {
        console.log('[WebSocket] Connection closed intentionally - not reconnecting')
        updateConnectionState({ status: 'disconnected' })
        reconnectAttempt = 0
        return
      }

      // Unexpected disconnection (including server deploy/restart) - reconnect
      console.log('[WebSocket] Unexpected disconnection detected - will attempt reconnect')
      attemptReconnect()
    }
  }

  function close(): void {
    // Prevent reconnection when intentionally closing
    shouldReconnect = false

    // Clear any pending reconnect timer
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    // Stop heartbeat
    stopHeartbeat()

    if (ws) {
      try {
        ws.close(1000, 'Client closed connection')
      } finally {
        ws = null
        messageQueue.length = 0
        updateConnectionState({ status: 'disconnected' })
      }
    }
  }

  function onMessage(cb: MessageListener): void {
    listeners.add(cb)
  }

  function offMessage(cb: MessageListener): void {
    listeners.delete(cb)
  }

  function onOpen(cb: () => void): void {
    openListeners.add(cb)
  }

  function offOpen(cb: () => void): void {
    openListeners.delete(cb)
  }

  function onConnectionStateChange(cb: ConnectionStateListener): void {
    connectionStateListeners.add(cb)
  }

  function offConnectionStateChange(cb: ConnectionStateListener): void {
    connectionStateListeners.delete(cb)
  }

  function getConnectionState(): ConnectionState {
    return connectionState
  }

  function send(msg: ClientToServerMessage): void {
    if (!ws) throw new Error('WS_NOT_CONNECTED')

    // Check if WebSocket is OPEN before sending
    if (ws.readyState === WebSocket.OPEN) {
      // Connection is ready - send immediately
      ws.send(JSON.stringify(msg))
    } else if (ws.readyState === WebSocket.CONNECTING) {
      // Connection is still opening - queue the message
      messageQueue.push(msg)

      // Safety timeout - if connection doesn't open in 5 seconds, clear queue
      setTimeout(() => {
        const idx = messageQueue.indexOf(msg)
        if (idx !== -1) {
          messageQueue.splice(idx, 1)
        }
      }, 5000)
    } else {
      // Connection is closing or closed - throw error
      throw new Error('WS_NOT_READY')
    }
  }

  const startSession = (classroomId: string, lessonId: string, lessonName: string, eduqueryLessonId: string): void => {
    send({ type: 'session:start', classroomId, lessonId, lessonName, eduqueryLessonId })
  }

  return {
    connect,
    close,
    onMessage,
    offMessage,
    send,
    onOpen,
    offOpen,
    onConnectionStateChange,
    offConnectionStateChange,
    getConnectionState,
    startSession
  }
}

export default createWsClient


